import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useData } from "../context/DataContext";
import { CONTACT, LOGO } from "../mock/mock";

const Header = () => {
  const { lang, toggle, tr, ui } = useLang();
  const { categories } = useData();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [colOpen, setColOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const infoLinks = [
    { to: "/about", label: ui.nav.about },
    { to: "/heritage", label: ui.nav.heritage },
    { to: "/how-we-do", label: ui.nav.process },
    { to: "/nasil-uygulanir", label: ui.nav.apply },
    { to: "/contact", label: ui.nav.contactInfo },
  ];
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const solid = scrolled || !isHome;
  const textCol = solid ? "text-[#211d18]" : "text-white";

  const NavLink = ({ to, children }) => (
    <Link to={to} className={`hover-underline text-[13px] uppercase tracking-[0.16em] font-medium ${textCol}`}>{children}</Link>
  );

  return (
    <>
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${solid ? "bg-[#f7f3ee]/95 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.06)] py-3" : "bg-transparent py-5"}`}>
      <div className="max-w-[1360px] mx-auto px-5 md:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src={LOGO} alt="HAAS Mosaic" className={`h-10 md:h-12 w-auto transition-all duration-300 ${solid ? "" : "brightness-0 invert"}`} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          <NavLink to="/">{ui.nav.home}</NavLink>
          <div className="relative" onMouseEnter={() => setColOpen(true)} onMouseLeave={() => setColOpen(false)}>
            <button onClick={() => { setColOpen((v) => !v); setInfoOpen(false); }} className={`flex items-center gap-1 hover-underline text-[13px] uppercase tracking-[0.16em] font-medium ${textCol}`}>
              {ui.nav.categories} <ChevronDown size={14} />
            </button>
            {colOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                <div className="bg-[#f7f3ee] shadow-xl border border-[#e6ddd0] w-64 py-3 animate-fade-up">
                  {categories.map((c) => (
                    <Link key={c.slug} to={`/product-category/${c.slug}`} className="block px-6 py-2.5 text-[12px] uppercase tracking-[0.14em] text-[#211d18] hover:bg-[#efe6d9] hover:text-[#a67c52] transition-colors">{tr(c.name)}</Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/all-projects">{ui.nav.projects}</NavLink>
          <NavLink to="/akademi">{ui.nav.academy}</NavLink>
          <div className="relative" onMouseEnter={() => setInfoOpen(true)} onMouseLeave={() => setInfoOpen(false)}>
            <button onClick={() => { setInfoOpen((v) => !v); setColOpen(false); }} className={`flex items-center gap-1 hover-underline text-[13px] uppercase tracking-[0.16em] font-medium ${textCol}`}>
              {ui.nav.info} <ChevronDown size={14} />
            </button>
            {infoOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                <div className="bg-[#f7f3ee] shadow-xl border border-[#e6ddd0] w-60 py-3 animate-fade-up">
                  {infoLinks.map((l) => (
                    <Link key={l.to} to={l.to} className="block px-6 py-2.5 text-[12px] uppercase tracking-[0.14em] text-[#211d18] hover:bg-[#efe6d9] hover:text-[#a67c52] transition-colors">{l.label}</Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-4">
          <button onClick={toggle} className={`flex items-center gap-1.5 text-[12px] uppercase tracking-[0.14em] font-medium ${textCol} hover:text-[#a67c52] transition-colors`}>
            <Globe size={15} /> {lang === "tr" ? "EN" : "TR"}
          </button>
          <button className={`lg:hidden ${textCol}`} onClick={() => setOpen(true)} aria-label="menu"><Menu size={26} /></button>
        </div>
      </div>
    </header>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-[60] lg:hidden transition-all duration-300 ${open ? "visible" : "invisible"}`}>
        <div className={`absolute inset-0 bg-black/40 transition-opacity ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-[82%] max-w-sm bg-[#f7f3ee] shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"} overflow-y-auto`}>
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#e6ddd0]">
            <img src={LOGO} alt="HAAS Mosaic" className="h-9 w-auto" />
            <button onClick={() => setOpen(false)}><X size={26} /></button>
          </div>
          <div className="flex flex-col px-6 py-4">
            <Link to="/" className="py-3 text-[15px] uppercase tracking-[0.14em] border-b border-[#e6ddd0]">{ui.nav.home}</Link>
            <div className="py-3 border-b border-[#e6ddd0]">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#a67c52] mb-2">{ui.nav.categories}</p>
              {categories.map((c) => (
                <Link key={c.slug} to={`/product-category/${c.slug}`} className="block py-2 text-[13px] text-[#4a443c]">{tr(c.name)}</Link>
              ))}
            </div>
            <Link to="/all-projects" className="py-3 text-[15px] uppercase tracking-[0.14em] border-b border-[#e6ddd0]">{ui.nav.projects}</Link>
            <Link to="/akademi" className="py-3 text-[15px] uppercase tracking-[0.14em] border-b border-[#e6ddd0]">{ui.nav.academy}</Link>
            <div className="py-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#a67c52] mb-2">{ui.nav.info}</p>
              {infoLinks.map((l) => (
                <Link key={l.to} to={l.to} className="block py-2 text-[13px] text-[#4a443c]">{l.label}</Link>
              ))}
            </div>
            <button onClick={toggle} className="flex items-center gap-2 py-3 text-[14px] uppercase tracking-[0.14em]"><Globe size={16} /> {lang === "tr" ? "English" : "Türkçe"}</button>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="mt-3 text-center bg-[#a67c52] text-white text-[13px] uppercase tracking-[0.14em] py-3">{ui.getInTouch}</a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
